import slugify from "@sindresorhus/slugify";

import { validateEmail } from "@/utils/validateEmail";

const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 40;

const NAME_MAX_LENGTH = 100;

/**
 * Generates a username from a full name. Transliterates to Latin (e.g. Cyrillic → Latin),
 * keeps letters, digits, dots, underscores; other characters (including spaces) become "-".
 * Result is lowercased and only contains A-Za-z0-9._-
 * @see apps/api/config/initializers/constants.rb USERNAME_FORMAT (USERNAME_CHARS)
 */
export function generateUsername(fullname: string): string {
  const trimmed = fullname.slice(0, NAME_MAX_LENGTH).trim();
  if (!trimmed) return "";
  return slugify(trimmed, {
    separator: "-",
    decamelize: false,
    preserveCharacters: [".", "_"],
  });
}

// @see apps/api/config/initializers/constants.rb
// Make sure it is in sync with USERNAME_FORMAT
const USERNAME_REGEX = new RegExp(
  `^[A-Za-z0-9\\-_.]{${USERNAME_MIN_LENGTH},${USERNAME_MAX_LENGTH}}$`,
);

export function validateName(value: string) {
  if (value.length === 0) {
    return "auth.errors.fullName.empty";
  }
  if (value.length > NAME_MAX_LENGTH) {
    return "auth.errors.fullName.tooLong";
  }
  return null;
}

export function validateUsername(value: string) {
  if (value.length === 0) {
    return "auth.errors.userName.empty";
  }
  if (value.length > USERNAME_MAX_LENGTH) {
    return "auth.errors.userName.tooLong";
  }
  if (value.length < USERNAME_MIN_LENGTH) {
    return "auth.errors.userName.tooShort";
  }
  if (!USERNAME_REGEX.test(value)) {
    return "auth.errors.userName.invalid";
  }
  return null;
}

export const EMAIL_VERIFICATION_CODE_LENGTH = 6;

export function validateUserEmail(value: string) {
  if (value.length === 0) {
    return "auth.errors.email.empty";
  }
  if (!validateEmail(value)) {
    return "auth.errors.email.invalid";
  }
  return null;
}
